import {
  isContentRating,
  isHubContentKind,
  hubItemKindFromLegacyContentKind,
  legacyContentKindFromHubItemKind,
  normalizeStars,
  hubItemDtoFromListing,
  type ContentRating,
  type HubComment,
  type HubContentKind,
  type HubItemDto,
  type HubListing,
  type HubListingDetail,
  type HubPage,
  type HubProfile,
  type HubPublishInput,
  type HubQuery,
} from "@roqeet/core";
import { RepositoryHubClient } from "./hub-client.js";
import type {
  AuthRepository,
  FeedbackRepository,
  ModerationRepository,
  ObjectStoragePort,
  ProfileRepository,
  ProjectRepository,
  StatsRepository,
} from "./ports.js";

/**
 * RoQeet Hub Supabase 어댑터 — 결정 #23/#31(+부록 A: Auth+DB+Storage). core의
 * {@link HubClient} 포트를 Supabase REST(PostgREST/GoTrue/Storage) 위에 raw fetch로
 * 구현한다 — Google Drive 백엔드(결정 #8)·OAuth(결정 #8)와 동일한 어댑터 스타일이라
 * 번들이 얇고 6s+에 안전하다(SDK 의존 0).
 *
 * 미구성(키 없음)이면 {@link isConfigured}가 false → UI가 허브 진입점을 숨긴다.
 * 익명도 **읽기**(목록/검색/상세/댓글/프로필)는 anon key로 가능하고, 쓰기(발행/평점/
 * 댓글/팔로우)만 로그인(플랫폼 계정, 결정 #31 tier 3)을 요구한다. 신원은 Google id_token을
 * Supabase GoTrue로 교환해 재활용한다(결정 #8/#31).
 *
 * 콘텐츠 정책(결정 #23/#31): NSFW 분리 노출은 질의에서 게이팅하고(includeNsfw),
 * 업로드 중재·CSAM 스캔은 백엔드(DB 트리거/엣지 함수) 책임이다 — 클라이언트는 신뢰
 * 경계가 아니다(결정 #17 정신).
 */

const SESSION_KEY = "roqeet.hub.supabase.session";
const THUMB_BUCKET = "hub-thumbnails";
const INLINE_PAYLOAD_PROVIDER = "inline_payload";
const INLINE_PAYLOAD_NAMESPACE = "listings.payload";

interface StoredSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  profile: HubProfile;
}

/** PostgREST가 author 임베드로 돌려주는 프로필 요약 행. */
interface RowAuthor {
  id: string;
  handle: string;
  avatar_url: string | null;
}

/** `hub_listings` 뷰 한 행(집계 포함). 무거운 payload는 상세 조회에서만 select한다. */
interface ListingRow {
  id: string;
  kind: string;
  title: string;
  summary: string | null;
  description: string | null;
  author: RowAuthor | null;
  author_id: string;
  tags: string[] | null;
  rating: string;
  thumbnail_url: string | null;
  version: string | null;
  license: string | null;
  public_id: string | null;
  download_count: number;
  rating_avg: number | null;
  rating_count: number;
  comment_count: number;
  view_count: number;
  bookmark_count: number;
  source_visibility: "public" | "hidden";
  stats_privacy: "public" | "private" | "uncollected";
  created_at: string;
  updated_at: string;
  payload?: string;
}

interface ProfileRow {
  id: string;
  handle: string;
  bio: string | null;
  avatar_url: string | null;
  created_at: string;
  listing_count?: number;
  follower_count?: number;
  following_count?: number;
}

interface CommentRow {
  id: string;
  listing_id: string;
  author: RowAuthor | null;
  author_id: string;
  body: string;
  created_at: string;
}

interface UploadedThumbnailMetadataInput {
  ownerId: string;
  objectKey: string;
  ext: string;
  mime: string;
  sizeBytes: number;
  publicUrl: string;
}

interface InlinePayloadMetadataInput {
  ownerId: string;
  listingId: string;
  payload: string;
}

/** epoch ms 정수로 안전 변환(ISO 문자열/숫자 모두 수용). */
function toMillis(v: string | number | null | undefined): number {
  if (typeof v === "number") return v;
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

function rating(v: string): ContentRating {
  return isContentRating(v) ? v : "unrated";
}
function kind(v: string): HubContentKind {
  if (!isHubContentKind(v)) throw new Error(`알 수 없는 콘텐츠 종류: ${v}`);
  return v;
}

function authorSummary(a: RowAuthor | null, fallbackId: string): HubListing["author"] {
  return {
    id: a?.id ?? fallbackId,
    handle: a?.handle ?? "익명",
    ...(a?.avatar_url ? { avatarUrl: a.avatar_url } : {}),
  };
}

function mapListing(row: ListingRow): HubListing {
  const legacyKind = kind(row.kind);
  const itemKind = hubItemKindFromLegacyContentKind(legacyKind);
  return {
    id: row.id,
    kind: legacyKind,
    itemKind,
    botSubtype: legacyKind === "character" ? "character" : null,
    itemStatus: "published",
    title: row.title,
    ...(row.summary ? { summary: row.summary } : {}),
    ...(row.description ? { description: row.description } : {}),
    author: authorSummary(row.author, row.author_id),
    tags: row.tags ?? [],
    rating: rating(row.rating),
    ...(row.thumbnail_url ? { thumbnailUrl: row.thumbnail_url } : {}),
    ...(row.version ? { version: row.version } : {}),
    ...(row.license ? { license: row.license } : {}),
    downloadCount: row.download_count ?? 0,
    ratingAvg: row.rating_avg ?? null,
    ratingCount: row.rating_count ?? 0,
    commentCount: row.comment_count ?? 0,
    viewCount: row.view_count ?? 0,
    bookmarkCount: row.bookmark_count ?? 0,
    sourceVisibility: row.source_visibility ?? "public",
    statsPrivacy: row.stats_privacy ?? "public",
    createdAt: toMillis(row.created_at),
    updatedAt: toMillis(row.updated_at),
  };
}

function mapProfile(row: ProfileRow): HubProfile {
  return {
    id: row.id,
    handle: row.handle,
    ...(row.bio ? { bio: row.bio } : {}),
    ...(row.avatar_url ? { avatarUrl: row.avatar_url } : {}),
    createdAt: toMillis(row.created_at),
    ...(typeof row.listing_count === "number" ? { listingCount: row.listing_count } : {}),
    ...(typeof row.follower_count === "number" ? { followerCount: row.follower_count } : {}),
    ...(typeof row.following_count === "number" ? { followingCount: row.following_count } : {}),
  };
}

function mapComment(row: CommentRow, viewerId: string | null): HubComment {
  return {
    id: row.id,
    listingId: row.listing_id,
    author: authorSummary(row.author, row.author_id),
    body: row.body,
    createdAt: toMillis(row.created_at),
    ...(viewerId && row.author_id === viewerId ? { viewerIsAuthor: true } : {}),
  };
}

/** PostgREST `*`(contains/ilike) 안전 이스케이프: 쉼표·괄호가 필터 문법을 깨지 않게. */
function escapeFilterValue(v: string): string {
  return v.replace(/[(),]/g, " ").trim();
}

function inlinePayloadObjectKey(listingId: string): string {
  return `listings/${listingId}/payload`;
}

async function sha256Hex(bytes: Uint8Array): Promise<string | undefined> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return undefined;
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    const digest = await subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
  } catch {
    return undefined;
  }
}

const PAGE_SIZE = 24;

class SupabaseHubAdapter implements
  AuthRepository,
  ProfileRepository,
  ProjectRepository,
  FeedbackRepository,
  ModerationRepository,
  StatsRepository,
  ObjectStoragePort {
  private readonly baseUrl: string;
  private readonly anonKey: string;
  private session: StoredSession | null;
  private readonly listeners = new Set<(p: HubProfile | null) => void>();

  constructor(baseUrl?: string, anonKey?: string) {
    let url = (baseUrl ?? import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/+$/, "");
    if (url.endsWith("/rest/v1")) {
      url = url.slice(0, -8);
    }
    this.baseUrl = url;
    this.anonKey = anonKey ?? import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
    this.session = this.readStored();
    if (this.session) {
      this.refreshOwnProfileInBackground();
    }
  }

  isConfigured(): boolean {
    return this.baseUrl.length > 0 && this.anonKey.length > 0;
  }

  // ---- 세션 영속(localStorage, 결정 #8 정직성: 순수 클라 앱 한계 수용) ----

  private readStored(): StoredSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      return null;
    }
  }
  private writeStored(s: StoredSession | null): void {
    try {
      if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      /* private mode: 비영속 */
    }
  }
  private emit(): void {
    const p = this.session?.profile ?? null;
    for (const fn of this.listeners) fn(p);
  }

  // ---- 저수준 REST 헬퍼 ----

  /** 인증 헤더(로그인 시 사용자 토큰, 아니면 anon key). 만료 전이면 토큰을 갱신한다. */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    if (this.session?.expiresAt && this.session?.refreshToken) {
      // 1분 전 만료 임박이면 리프레시 시도
      if (Date.now() > this.session.expiresAt - 60000) {
        try {
          const res = await fetch(`${this.baseUrl}/auth/v1/token?grant_type=refresh_token`, {
            method: "POST",
            headers: { apikey: this.anonKey, "content-type": "application/json" },
            body: JSON.stringify({ refresh_token: this.session.refreshToken }),
          });
          if (res.ok) {
            const data = await res.json() as { access_token: string; refresh_token?: string; expires_in?: number };
            this.session.accessToken = data.access_token;
            if (data.refresh_token) this.session.refreshToken = data.refresh_token;
            if (data.expires_in) this.session.expiresAt = Date.now() + data.expires_in * 1000;
            this.writeStored(this.session);
          } else {
            // 갱신 거부됨 (예: 세션 무효화) -> 로그아웃 처리
            this.session = null;
            this.writeStored(null);
            this.emit();
          }
        } catch (e) {
          // 네트워크 에러 시 기존 토큰 그대로 시도
        }
      }
    }
    const token = this.session?.accessToken ?? this.anonKey;
    return { apikey: this.anonKey, authorization: `Bearer ${token}` };
  }

  private ensureConfigured(): void {
    if (!this.isConfigured()) {
      throw new Error("허브 백엔드가 설정되지 않았습니다(VITE_SUPABASE_URL/ANON_KEY).");
    }
  }
  private ensureSignedIn(): StoredSession {
    this.ensureConfigured();
    if (!this.session) throw new Error("이 작업은 로그인이 필요합니다.");
    return this.session;
  }

  /** PostgREST 요청. path는 `listings?...` 형태. */
  private async rest<T>(path: string, init?: RequestInit): Promise<T> {
    this.ensureConfigured();
    const res = await fetch(`${this.baseUrl}/rest/v1/${path}`, {
      ...init,
      headers: {
        ...(await this.getAuthHeaders()),
        "content-type": "application/json",
        ...(init?.headers as Record<string, string> | undefined),
      },
    });
    if (!res.ok) {
      throw new Error(`허브 요청 실패(${res.status}): ${await res.text()}`);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  // ---- 인증(GoTrue, 결정 #31) ----

  currentProfile(): HubProfile | null {
    return this.session?.profile ?? null;
  }

  subscribe(onChange: (profile: HubProfile | null) => void): () => void {
    this.listeners.add(onChange);
    onChange(this.session?.profile ?? null);
    return () => this.listeners.delete(onChange);
  }

  /**
   * Google id_token을 Supabase GoTrue로 교환해 허브 세션을 만든다(결정 #8/#31).
   * 신규 사용자는 백엔드 트리거(handle_new_user)가 profiles 행을 생성한다 — 없으면
   * email 앞부분으로 기본 핸들을 만든다.
   */
  async signIn(idToken: string): Promise<HubProfile> {
    this.ensureConfigured();
    const res = await fetch(`${this.baseUrl}/auth/v1/token?grant_type=id_token`, {
      method: "POST",
      headers: { apikey: this.anonKey, "content-type": "application/json" },
      body: JSON.stringify({ provider: "google", id_token: idToken }),
    });
    if (!res.ok) {
      throw new Error(`허브 로그인 실패(${res.status}): ${await res.text()}`);
    }
    const data = (await res.json()) as {
      access_token: string;
      refresh_token?: string;
      expires_in?: number;
      user: { id: string; email?: string };
    };

    // 토큰을 먼저 세팅해야 이후 rest() 호출이 사용자 권한으로 나간다.
    this.session = {
      accessToken: data.access_token,
      ...(data.refresh_token ? { refreshToken: data.refresh_token } : {}),
      ...(data.expires_in ? { expiresAt: Date.now() + data.expires_in * 1000 } : {}),
      // 임시 프로필(아래에서 실제 행으로 교체).
      profile: {
        id: data.user.id,
        handle: data.user.email?.split("@")[0] ?? "user",
        createdAt: Date.now(),
      },
    };

    const profile = await this.fetchOwnProfile(data.user.id).catch(() => this.session!.profile);
    this.session = { ...this.session, profile };
    this.writeStored(this.session);
    this.emit();
    return profile;
  }

  async signOut(): Promise<void> {
    const token = this.session?.accessToken;
    this.session = null;
    this.writeStored(null);
    this.emit();
    // GoTrue 로그아웃은 best-effort(로컬 세션은 이미 해제됨).
    if (token && this.isConfigured()) {
      await fetch(`${this.baseUrl}/auth/v1/logout`, {
        method: "POST",
        headers: { apikey: this.anonKey, authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }

  private async fetchOwnProfile(userId: string): Promise<HubProfile> {
    const rows = await this.rest<ProfileRow[]>(
      `profiles?id=eq.${encodeURIComponent(userId)}&select=*&limit=1`,
    );
    if (rows[0]) return mapProfile(rows[0]);
    throw new Error("프로필을 찾을 수 없습니다.");
  }

  private async refreshOwnProfileInBackground(): Promise<void> {
    if (!this.session) return;
    try {
      const profile = await this.fetchOwnProfile(this.session.profile.id);
      if (this.session) {
        this.session = { ...this.session, profile };
        this.writeStored(this.session);
        this.emit();
      }
    } catch (e) {
      // 실패 시 무시 (토큰 무효화/네트워크 오류 등)
    }
  }

  // ---- 콘텐츠 배포(결정 #23) ----

  async list(query: HubQuery): Promise<HubPage<HubListing>> {
    const limit = query.limit ?? PAGE_SIZE;
    const offset = query.cursor ? Math.max(0, parseInt(query.cursor, 10) || 0) : 0;
    const legacyKindFilter = query.itemKind
      ? legacyContentKindFromHubItemKind(query.itemKind)
      : query.kind;
    if (query.itemKind && !legacyKindFilter) {
      return { items: [] };
    }

    const params: string[] = [
      "select=id,kind,title,summary,description,author_id,tags,rating,thumbnail_url,version,license,public_id,download_count,rating_avg,rating_count,comment_count,view_count,bookmark_count,source_visibility,stats_privacy,created_at,updated_at,author:profiles!listings_author_id_fkey(id,handle,avatar_url)",
    ];

    // NSFW 게이팅(결정 #23): 기본 SFW만. includeNsfw일 때만 nsfw 포함.
    if (!query.includeNsfw) params.push("rating=neq.nsfw");
    if (legacyKindFilter) params.push(`kind=eq.${legacyKindFilter}`);
    if (query.authorId) params.push(`author_id=eq.${encodeURIComponent(query.authorId)}`);
    if (query.tag) params.push(`tags=cs.{${encodeURIComponent(escapeFilterValue(query.tag))}}`);
    if (query.text) {
      const term = escapeFilterValue(query.text);
      if (term) {
        const enc = encodeURIComponent(`*${term}*`);
        params.push(`or=(title.ilike.${enc},summary.ilike.${enc})`);
      }
    }

    params.push(`order=${sortColumn(query.sort)}`);
    params.push(`limit=${limit}`);
    params.push(`offset=${offset}`);

    const rows = await this.rest<ListingRow[]>(`listings?${params.join("&")}`);
    const items = rows.map(mapListing);
    return {
      items,
      ...(items.length === limit ? { nextCursor: String(offset + limit) } : {}),
    };
  }

  async getHubItem(idOrSlug: string): Promise<HubItemDto | null> {
    const id = encodeURIComponent(idOrSlug);
    const rows = await this.rest<ListingRow[]>(
      `listings?id=eq.${id}&select=id,kind,title,summary,description,author_id,tags,rating,thumbnail_url,version,license,public_id,download_count,rating_avg,rating_count,comment_count,view_count,bookmark_count,source_visibility,stats_privacy,created_at,updated_at,author:profiles!listings_author_id_fkey(id,handle,avatar_url)&limit=1`,
    );
    const row = rows[0];
    return row ? hubItemDtoFromListing(mapListing(row)) : null;
  }

  async download(listingId: string): Promise<HubListingDetail> {
    const id = encodeURIComponent(listingId);
    const rows = await this.rest<ListingRow[]>(
      `listings?id=eq.${id}&select=*,author:profiles!listings_author_id_fkey(id,handle,avatar_url)&limit=1`,
    );
    const row = rows[0];
    if (!row) throw new Error("발행물을 찾을 수 없습니다.");
    if (typeof row.payload !== "string") {
      throw new Error("발행물 페이로드가 비어 있습니다.");
    }

    // 다운로드 카운트 증가(best-effort, RPC). 실패해도 설치는 진행.
    await this.rest("rpc/increment_download", {
      method: "POST",
      body: JSON.stringify({ p_listing_id: listingId }),
    }).catch(() => {});

    return { ...mapListing(row), payload: row.payload };
  }

  async publish(input: HubPublishInput): Promise<HubListing> {
    const session = this.ensureSignedIn();

    let thumbnailUrl: string | undefined;
    if (input.thumbnail) {
      thumbnailUrl = await this.uploadPublicImage(input.thumbnail, session.profile.id).catch(
        () => undefined,
      );
    }

    const body = {
      kind: input.kind,
      title: input.title,
      summary: input.summary ?? null,
      description: input.description ?? null,
      author_id: session.profile.id,
      tags: input.tags,
      rating: input.rating,
      version: input.version ?? null,
      license: input.license ?? null,
      public_id: input.publicId ?? null,
      source_visibility: input.sourceVisibility ?? "public",
      stats_privacy: input.statsPrivacy ?? "public",
      payload: input.payload,
      ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}),
    };

    const rows = await this.rest<ListingRow[]>(`listings`, {
      method: "POST",
      headers: { prefer: "return=representation" },
      body: JSON.stringify(body),
    });
    const row = rows[0];
    if (!row) throw new Error("발행에 실패했습니다.");
    if (thumbnailUrl) {
      await this.linkThumbnailMetadata(session.profile.id, row.id, thumbnailUrl).catch((e) => {
        console.warn("[hub] thumbnail metadata link failed", e);
      });
    }
    await this.upsertInlinePayloadMetadata({
      ownerId: session.profile.id,
      listingId: row.id,
      payload: input.payload,
    }).catch((e) => {
      console.warn("[hub] inline payload metadata write failed", e);
    });
    // 새 발행물은 집계가 0/기본값. author 임베드가 없으므로 세션 프로필로 채운다.
    return mapListing({ ...row, author: { id: session.profile.id, handle: session.profile.handle, avatar_url: session.profile.avatarUrl ?? null } });
  }

  async unpublish(listingId: string): Promise<void> {
    this.ensureSignedIn();
    await this.rest(`listings?id=eq.${encodeURIComponent(listingId)}`, { method: "DELETE" });
  }

  async uploadPublicImage(thumb: Blob, userId: string): Promise<string> {
    this.ensureSignedIn();
    const ext = thumb.type.split("/")[1]?.replace("jpeg", "jpg") ?? "png";
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;
    const res = await fetch(
      `${this.baseUrl}/storage/v1/object/${THUMB_BUCKET}/${path}`,
      {
        method: "POST",
        headers: {
          ...(await this.getAuthHeaders()),
          "content-type": thumb.type || "application/octet-stream",
          "x-upsert": "true",
        },
        body: thumb,
      },
    );
    if (!res.ok) throw new Error(`썸네일 업로드 실패(${res.status})`);
    const publicUrl = `${this.baseUrl}/storage/v1/object/public/${THUMB_BUCKET}/${path}`;
    await this.insertThumbnailMetadata({
      ownerId: userId,
      objectKey: path,
      ext,
      mime: thumb.type || "application/octet-stream",
      sizeBytes: thumb.size,
      publicUrl,
    }).catch((e) => {
      console.warn("[hub] thumbnail metadata insert failed", e);
    });
    return publicUrl;
  }

  private async insertThumbnailMetadata(input: UploadedThumbnailMetadataInput): Promise<void> {
    await this.rest("file_metadata", {
      method: "POST",
      headers: { prefer: "return=minimal" },
      body: JSON.stringify({
        owner_id: input.ownerId,
        kind: "thumbnail",
        visibility: "public",
        lifecycle: "uploaded",
        provider: "supabase_storage",
        storage_namespace: THUMB_BUCKET,
        object_key: input.objectKey,
        ext: input.ext,
        mime: input.mime,
        size_bytes: input.sizeBytes,
        legacy_url: input.publicUrl,
      }),
    });
  }

  private async linkThumbnailMetadata(
    ownerId: string,
    listingId: string,
    thumbnailUrl: string,
  ): Promise<void> {
    const params = [
      `owner_id=eq.${encodeURIComponent(ownerId)}`,
      "kind=eq.thumbnail",
      "provider=eq.supabase_storage",
      `storage_namespace=eq.${encodeURIComponent(THUMB_BUCKET)}`,
      `legacy_url=eq.${encodeURIComponent(thumbnailUrl)}`,
    ];
    await this.rest(`file_metadata?${params.join("&")}`, {
      method: "PATCH",
      headers: { prefer: "return=minimal" },
      body: JSON.stringify({
        listing_id: listingId,
        lifecycle: "public",
        linked_at: new Date().toISOString(),
      }),
    });
  }

  private async upsertInlinePayloadMetadata(input: InlinePayloadMetadataInput): Promise<void> {
    const bytes = new TextEncoder().encode(input.payload);
    const hash = await sha256Hex(bytes);
    const objectKey = inlinePayloadObjectKey(input.listingId);
    const params = [
      `owner_id=eq.${encodeURIComponent(input.ownerId)}`,
      `listing_id=eq.${encodeURIComponent(input.listingId)}`,
      `provider=eq.${encodeURIComponent(INLINE_PAYLOAD_PROVIDER)}`,
      `storage_namespace=eq.${encodeURIComponent(INLINE_PAYLOAD_NAMESPACE)}`,
      `object_key=eq.${encodeURIComponent(objectKey)}`,
      "select=id",
      "limit=1",
    ];
    const rows = await this.rest<Array<{ id: string }>>(`file_metadata?${params.join("&")}`);
    const commonBody = {
      visibility: "public",
      lifecycle: "linked",
      size_bytes: bytes.byteLength,
      hash_sha256: hash ?? null,
    };
    const existing = rows[0];
    if (existing) {
      await this.rest(`file_metadata?id=eq.${encodeURIComponent(existing.id)}`, {
        method: "PATCH",
        headers: { prefer: "return=minimal" },
        body: JSON.stringify(commonBody),
      });
      return;
    }

    const insertBody = {
      ...commonBody,
      owner_id: input.ownerId,
      listing_id: input.listingId,
      kind: "project_package",
      provider: INLINE_PAYLOAD_PROVIDER,
      storage_namespace: INLINE_PAYLOAD_NAMESPACE,
      object_key: objectKey,
      ext: "json",
      mime: "application/json",
      legacy_url: null,
      linked_at: new Date().toISOString(),
    };
    await this.rest("file_metadata", {
      method: "POST",
      headers: { prefer: "return=minimal" },
      body: JSON.stringify(insertBody),
    }).catch(async () => {
      await this.rest(`file_metadata?${params.slice(0, 5).join("&")}`, {
        method: "PATCH",
        headers: { prefer: "return=minimal" },
        body: JSON.stringify(commonBody),
      });
    });
  }

  // ---- 소셜 레이어(결정 #31) ----

  async getProfile(profileId: string): Promise<HubProfile | undefined> {
    const rows = await this.rest<ProfileRow[]>(
      `profiles?id=eq.${encodeURIComponent(profileId)}&select=*&limit=1`,
    );
    return rows[0] ? mapProfile(rows[0]) : undefined;
  }

  async updateProfile(
    patch: Partial<Pick<HubProfile, "handle" | "bio">>,
  ): Promise<HubProfile> {
    const session = this.ensureSignedIn();
    const body: Record<string, unknown> = {};
    if (patch.handle !== undefined) body["handle"] = patch.handle;
    if (patch.bio !== undefined) body["bio"] = patch.bio;
    const rows = await this.rest<ProfileRow[]>(
      `profiles?id=eq.${encodeURIComponent(session.profile.id)}`,
      {
        method: "PATCH",
        headers: { prefer: "return=representation" },
        body: JSON.stringify(body),
      },
    );
    const profile = rows[0] ? mapProfile(rows[0]) : session.profile;
    this.session = { ...session, profile };
    this.writeStored(this.session);
    this.emit();
    return profile;
  }

  async rate(listingId: string, stars: number): Promise<void> {
    const session = this.ensureSignedIn();
    const n = normalizeStars(stars); // core 검증 재사용(1~5)
    // 업서트: 같은 (listing_id, user_id)면 갱신.
    await this.rest(`ratings`, {
      method: "POST",
      headers: { prefer: "resolution=merge-duplicates" },
      body: JSON.stringify({ listing_id: listingId, user_id: session.profile.id, stars: n }),
    });
  }

  async listComments(listingId: string, cursor?: string): Promise<HubPage<HubComment>> {
    const limit = PAGE_SIZE;
    const offset = cursor ? Math.max(0, parseInt(cursor, 10) || 0) : 0;
    const rows = await this.rest<CommentRow[]>(
      `comments?listing_id=eq.${encodeURIComponent(listingId)}&status=eq.visible` +
        `&select=id,listing_id,author_id,body,created_at,author:profiles!comments_author_id_fkey(id,handle,avatar_url)` +
        `&order=created_at.desc&limit=${limit}&offset=${offset}`,
    );
    const viewerId = this.session?.profile.id ?? null;
    const items = rows.map((r) => mapComment(r, viewerId));
    return {
      items,
      ...(items.length === limit ? { nextCursor: String(offset + limit) } : {}),
    };
  }

  async addComment(listingId: string, body: string): Promise<HubComment> {
    const session = this.ensureSignedIn();
    const trimmed = body.trim();
    if (!trimmed) throw new Error("댓글 내용을 입력하세요.");
    const rows = await this.rest<CommentRow[]>(`comments`, {
      method: "POST",
      headers: { prefer: "return=representation" },
      body: JSON.stringify({
        listing_id: listingId,
        author_id: session.profile.id,
        body: trimmed,
      }),
    });
    const row = rows[0];
    if (!row) throw new Error("댓글 작성에 실패했습니다.");
    return mapComment(
      { ...row, author: { id: session.profile.id, handle: session.profile.handle, avatar_url: session.profile.avatarUrl ?? null } },
      session.profile.id,
    );
  }

  async deleteComment(commentId: string): Promise<void> {
    this.ensureSignedIn();
    await this.rest(`comments?id=eq.${encodeURIComponent(commentId)}`, { method: "DELETE" });
  }

  async follow(profileId: string): Promise<void> {
    const session = this.ensureSignedIn();
    await this.rest(`follows`, {
      method: "POST",
      headers: { prefer: "resolution=ignore-duplicates" },
      body: JSON.stringify({ follower_id: session.profile.id, following_id: profileId }),
    });
  }

  async unfollow(profileId: string): Promise<void> {
    const session = this.ensureSignedIn();
    await this.rest(
      `follows?follower_id=eq.${encodeURIComponent(session.profile.id)}` +
        `&following_id=eq.${encodeURIComponent(profileId)}`,
      { method: "DELETE" },
    );
  }

  // ---- 콘텐츠 관리(작성자 본인) ----

  async updateListing(listingId: string, patch: Partial<HubPublishInput>): Promise<HubListing> {
    const session = this.ensureSignedIn();
    let thumbnailUrl: string | undefined;
    if (patch.thumbnail) {
      thumbnailUrl = await this.uploadPublicImage(patch.thumbnail, session.profile.id).catch(
        () => undefined,
      );
    }
    const body: Record<string, unknown> = {};
    if (patch.title !== undefined) body["title"] = patch.title;
    if (patch.summary !== undefined) body["summary"] = patch.summary;
    if (patch.description !== undefined) body["description"] = patch.description;
    if (patch.tags !== undefined) body["tags"] = patch.tags;
    if (patch.rating !== undefined) body["rating"] = patch.rating;
    if (patch.version !== undefined) body["version"] = patch.version;
    if (patch.license !== undefined) body["license"] = patch.license;
    if (patch.sourceVisibility !== undefined) body["source_visibility"] = patch.sourceVisibility;
    if (patch.statsPrivacy !== undefined) body["stats_privacy"] = patch.statsPrivacy;
    if (patch.payload !== undefined) body["payload"] = patch.payload;
    if (thumbnailUrl !== undefined) body["thumbnail_url"] = thumbnailUrl;

    const rows = await this.rest<ListingRow[]>(`listings?id=eq.${encodeURIComponent(listingId)}`, {
      method: "PATCH",
      headers: { prefer: "return=representation" },
      body: JSON.stringify(body),
    });
    const row = rows[0];
    if (!row) throw new Error("수정에 실패했습니다.");
    if (thumbnailUrl) {
      await this.linkThumbnailMetadata(session.profile.id, row.id, thumbnailUrl).catch((e) => {
        console.warn("[hub] thumbnail metadata link failed", e);
      });
    }
    if (patch.payload !== undefined) {
      await this.upsertInlinePayloadMetadata({
        ownerId: session.profile.id,
        listingId: row.id,
        payload: patch.payload,
      }).catch((e) => {
        console.warn("[hub] inline payload metadata write failed", e);
      });
    }
    return mapListing({ ...row, author: { id: session.profile.id, handle: session.profile.handle, avatar_url: session.profile.avatarUrl ?? null } });
  }

  // ---- 북마크/조회 ----

  async bookmark(listingId: string): Promise<void> {
    const session = this.ensureSignedIn();
    await this.rest(`bookmarks`, {
      method: "POST",
      headers: { prefer: "resolution=ignore-duplicates" },
      body: JSON.stringify({ listing_id: listingId, user_id: session.profile.id }),
    });
  }

  async unbookmark(listingId: string): Promise<void> {
    const session = this.ensureSignedIn();
    await this.rest(
      `bookmarks?listing_id=eq.${encodeURIComponent(listingId)}&user_id=eq.${encodeURIComponent(session.profile.id)}`,
      { method: "DELETE" },
    );
  }

  async incrementView(listingId: string): Promise<void> {
    await this.rest("rpc/increment_view", {
      method: "POST",
      body: JSON.stringify({ p_listing_id: listingId }),
    }).catch(() => {});
  }

  // ---- 작성자 프로필 ----

  async getProfileByHandle(handle: string): Promise<HubProfile | undefined> {
    const rows = await this.rest<ProfileRow[]>(
      `profiles?handle=eq.${encodeURIComponent(handle)}&select=*&limit=1`,
    );
    return rows[0] ? mapProfile(rows[0]) : undefined;
  }

  // ---- 관리자 전용(서버에서 is_admin 검증) ----

  async checkAdmin(): Promise<boolean> {
    if (!this.session) return false;
    try {
      const res = await this.rest<boolean>("rpc/check_admin", { method: "POST" });
      return res === true;
    } catch {
      return false;
    }
  }

  async adminListUsers(limit: number, offset: number): Promise<import("@roqeet/core").AdminUserInfo[]> {
    this.ensureSignedIn();
    return await this.rest<import("@roqeet/core").AdminUserInfo[]>("rpc/admin_list_users", {
      method: "POST",
      body: JSON.stringify({ p_limit: limit, p_offset: offset }),
    });
  }

  async adminRemoveListing(listingId: string): Promise<void> {
    this.ensureSignedIn();
    await this.rest("rpc/admin_remove_listing", {
      method: "POST",
      body: JSON.stringify({ p_listing_id: listingId }),
    });
  }
}

/** Supabase-backed repository adapter wired into the HubClient facade. */
export class SupabaseHubClient extends RepositoryHubClient {
  private readonly adapter: SupabaseHubAdapter;

  constructor(baseUrl?: string, anonKey?: string) {
    const adapter = new SupabaseHubAdapter(baseUrl, anonKey);
    super({
      auth: adapter,
      profiles: adapter,
      projects: adapter,
      feedback: adapter,
      moderation: adapter,
      stats: adapter,
    });
    this.adapter = adapter;
  }

  override getHubItem(idOrSlug: string): Promise<HubItemDto | null> {
    return this.adapter.getHubItem(idOrSlug);
  }
}

/** PostgREST order expression for listing sort options. */
function sortColumn(sort: HubQuery["sort"]): string {
  switch (sort) {
    case "popular":
      return "download_count.desc";
    case "top-rated":
      return "rating_avg.desc.nullslast";
    case "downloads":
      return "download_count.desc";
    case "views":
      return "view_count.desc";
    case "bookmarks":
      return "bookmark_count.desc";
    case "recent":
    default:
      return "created_at.desc";
  }
}
